// DataSourceModal — add or edit one data source: schema name, type and external
// id. `dataSourceType` stays free text because the wire carries ready-to-display
// strings rather than a fixed slug set, so a hardcoded picker could reject a
// value the backend accepts.
//
// One component for both modes rather than a Create/Edit pair, because PATCH
// datasource/:id takes the create body unchanged — the form is identical and
// only the seed, the copy and the endpoint differ (the DataSourceEntryModal
// idiom). Passing `dataSource` switches it to edit.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCreateDataSourceMutation,
  useUpdateDataSourceMutation,
} from '@/api/endpoints/datasourceApi';
import { toast } from 'react-hot-toast';
import { Button, Field, Input, Modal } from '@/components/common';

/** What an edit seeds from. Every field is part of the create payload and every
 *  one is already on a list row, so opening the editor needs no details fetch —
 *  which is also why a row selected on an earlier page can still be edited. */
export interface EditableDataSource {
  id: string;
  schemaName: string;
  dataSourceType: string;
  externalId: string;
}

interface DataSourceModalProps {
  open: boolean;
  onClose: () => void;
  /** Present → edit that source; absent → add a new one. */
  dataSource?: EditableDataSource;
}
const emptyDraft = {
  schemaName: '',
  dataSourceType: '',
  externalId: '',
};
const DataSourceModal = ({ open, onClose, dataSource }: DataSourceModalProps) => {
  const { t } = useTranslation();
  const [createDataSource, { isLoading: creating }] = useCreateDataSourceMutation();
  const [updateDataSource, { isLoading: updating }] = useUpdateDataSourceMutation();
  const [draft, setDraft] = useState(emptyDraft);
  const [submitted, setSubmitted] = useState(false);

  const editing = !!dataSource;
  const saving = creating || updating;

  // Re-seed each time the modal opens: the source's current values in edit mode,
  // empty in create. Discards leftovers from a previous open either way.
  useEffect(() => {
    if (open) {
      setDraft(
        dataSource
          ? {
              schemaName: dataSource.schemaName,
              dataSourceType: dataSource.dataSourceType,
              externalId: dataSource.externalId,
            }
          : emptyDraft,
      );
      setSubmitted(false);
    }
  }, [open, dataSource]);

  const patch = (changes: Partial<typeof emptyDraft>) => setDraft((d) => ({ ...d, ...changes }));

  // Test the SAME trimmed value that gets submitted — otherwise "   " passes
  // validation and posts an empty string.
  const schemaNameMissing = !draft.schemaName.trim();
  const dataSourceTypeMissing = !draft.dataSourceType.trim();
  const externalIdMissing = !draft.externalId.trim();

  const submit = async () => {
    // Required-field validation runs BEFORE any backend call: mark the form as
    // submitted so errors show, and bail while anything is invalid.
    setSubmitted(true);
    if (schemaNameMissing || dataSourceTypeMissing || externalIdMissing) return;
    const body = {
      schemaName: draft.schemaName.trim(),
      dataSourceType: draft.dataSourceType.trim(),
      externalId: draft.externalId.trim(),
    };
    try {
      if (dataSource) await updateDataSource({ id: dataSource.id, body }).unwrap();
      else await createDataSource(body).unwrap();
      toast.success(
        t(editing ? 'datasource_updated' : 'datasource_created', { name: body.schemaName }),
      );
      onClose();
    } catch {
      toast.error(t('error_generic'));
    }
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      icon={editing ? 'square-pen' : 'plus'}
      title={t(editing ? 'datasource_edit_title' : 'datasource_create_title')}
      subtitle={t(editing ? 'datasource_edit_subtitle' : 'datasource_create_subtitle')}
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={saving}>
            {t('button_cancel')}
          </Button>
          <Button iconStart={editing ? 'check' : 'plus'} onClick={submit} loading={saving}>
            {t(editing ? 'datasource_save' : 'datasource_create')}
          </Button>
        </>
      }
    >
      <div className='datasource-create'>
        <Field
          label={t('datasource_field_schema_name')}
          required
          error={submitted && schemaNameMissing ? t('datasource_schema_name_required') : undefined}
        >
          <Input
            value={draft.schemaName}
            placeholder={t('datasource_field_schema_name_placeholder')}
            error={submitted && schemaNameMissing}
            onChange={(e) => patch({ schemaName: e.target.value })}
          />
        </Field>
        <div className='datasource-create__row'>
          <Field
            label={t('datasource_field_type')}
            required
            error={
              submitted && dataSourceTypeMissing ? t('datasource_field_type_required') : undefined
            }
          >
            <Input
              value={draft.dataSourceType}
              placeholder={t('datasource_field_type_placeholder')}
              error={submitted && dataSourceTypeMissing}
              onChange={(e) => patch({ dataSourceType: e.target.value })}
            />
          </Field>
          <Field
            label={t('datasource_field_external_id')}
            required
            error={
              submitted && externalIdMissing ? t('datasource_external_id_required') : undefined
            }
          >
            <Input
              value={draft.externalId}
              placeholder={t('datasource_field_external_id_placeholder')}
              error={submitted && externalIdMissing}
              onChange={(e) => patch({ externalId: e.target.value })}
            />
          </Field>
        </div>
      </div>
    </Modal>
  );
};

export default DataSourceModal;
